import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import ModernProductSelectorV2 from "../../../utils/Selector/ModernProductSelectorV2";
import "./module-builder.css";

const STORAGE_PREFIX = "promang.offerCalculation.moduleGraph.";
const CANVAS_SIZE = 5000;
const DEFAULT_VIEWPORT = { x: 80, y: 60, zoom: 1 };

const NODE_DEFINITIONS = {
  service: {
    labelKey: "offer.calculation.module.node.service",
    descriptionKey: "offer.calculation.module.node.service_hint",
    defaultTitle: "Leilighet",
    defaultData: { amount: 4500, enabled: true },
  },
  quantity: {
    labelKey: "offer.calculation.module.node.quantity",
    descriptionKey: "offer.calculation.module.node.quantity_hint",
    defaultTitle: "Antall kilometer",
    defaultData: { quantity: 1, unitPrice: 25 },
  },
  productPrice: {
    labelKey: "offer.calculation.module.node.product_price",
    descriptionKey: "offer.calculation.module.node.product_price_hint",
    defaultTitle: "Produktpris",
    defaultData: { quantity: 1, unitPrice: 0, productName: "" },
  },
  productCatalog: {
    labelKey: "offer.calculation.module.node.product_catalog",
    descriptionKey: "offer.calculation.module.node.product_catalog_hint",
    defaultTitle: "Produkter",
    defaultData: { products: [] },
  },
  laborHours: {
    labelKey: "offer.calculation.module.node.labor_hours",
    descriptionKey: "offer.calculation.module.node.labor_hours_hint",
    defaultTitle: "Timer",
    defaultData: { hours: 1, hourlyRate: 883, costHourlyRate: 0 },
  },
  percentage: {
    labelKey: "offer.calculation.module.node.percentage",
    descriptionKey: "offer.calculation.module.node.percentage_hint",
    defaultTitle: "Prosent",
    defaultData: { percent: 10, scope: "all" },
  },
  distribution: {
    labelKey: "offer.calculation.module.node.distribution",
    descriptionKey: "offer.calculation.module.node.distribution_hint",
    defaultTitle: "Fordeling",
    defaultData: { divisor: 2 },
  },
  formula: {
    labelKey: "offer.calculation.module.node.formula",
    descriptionKey: "offer.calculation.module.node.formula_hint",
    defaultTitle: "Excel formel",
    defaultData: { expression: "input" },
  },
  subtotal: {
    labelKey: "offer.calculation.module.node.subtotal",
    descriptionKey: "offer.calculation.module.node.subtotal_hint",
    defaultTitle: "Subtotal",
    defaultData: {},
  },
  total: {
    labelKey: "offer.calculation.module.node.total",
    descriptionKey: "offer.calculation.module.node.total_hint",
    defaultTitle: "Total",
    defaultData: {},
  },
  note: {
    labelKey: "offer.calculation.module.node.note",
    descriptionKey: "offer.calculation.module.node.note_hint",
    defaultTitle: "Notat",
    defaultData: { note: "" },
  },
};

const FORMULA_VARIABLES = [
  { token: "input", labelKey: "offer.calculation.module.formula.variable.input" },
  { token: "quantity", labelKey: "offer.calculation.module.formula.variable.quantity" },
  { token: "price", labelKey: "offer.calculation.module.formula.variable.price" },
  { token: "percent", labelKey: "offer.calculation.module.formula.variable.percent" },
];

const FORMULA_FUNCTIONS = [
  { token: "SUM(input, 500)", labelKey: "offer.calculation.module.formula.function.sum" },
  { token: "ROUND(input)", labelKey: "offer.calculation.module.formula.function.round" },
  { token: "MIN(input, 10000)", labelKey: "offer.calculation.module.formula.function.min" },
  { token: "MAX(input, 2500)", labelKey: "offer.calculation.module.formula.function.max" },
];

const FORMULA_TEMPLATES = [
  { expression: "input", labelKey: "offer.calculation.module.formula.template.same" },
  { expression: "input + 500", labelKey: "offer.calculation.module.formula.template.fixed_add" },
  { expression: "input - 500", labelKey: "offer.calculation.module.formula.template.fixed_subtract" },
  { expression: "input * 1.1", labelKey: "offer.calculation.module.formula.template.ten_percent" },
  { expression: "input * (1 + percent / 100)", labelKey: "offer.calculation.module.formula.template.custom_percent" },
  { expression: "quantity * price", labelKey: "offer.calculation.module.formula.template.quantity_price" },
  { expression: "input / 2", labelKey: "offer.calculation.module.formula.template.split_two" },
  { expression: "ROUND(input)", labelKey: "offer.calculation.module.formula.template.round" },
];

const DEFAULT_GRAPH = {
  version: 1,
  viewport: DEFAULT_VIEWPORT,
  nodes: [
    {
      id: "service-apartment",
      type: "service",
      title: "Leilighet",
      x: 80,
      y: 120,
      data: { amount: 4500, enabled: true, lockedFields: { amount: true } },
    },
    {
      id: "kilometers",
      type: "quantity",
      title: "Antall kilometer",
      x: 80,
      y: 360,
      data: { quantity: 18, unitPrice: 25, lockedFields: { unitPrice: true } },
    },
    {
      id: "job-percent",
      type: "percentage",
      title: "Påslag hele jobben",
      x: 390,
      y: 210,
      data: { percent: 12, scope: "all" },
    },
    {
      id: "distribution",
      type: "distribution",
      title: "Fordeling på X",
      x: 680,
      y: 210,
      data: { divisor: 2 },
    },
    {
      id: "total",
      type: "total",
      title: "Full service total",
      x: 950,
      y: 250,
      data: { lockedFields: {} },
    },
  ],
  edges: [
    { id: "edge-service-percent", source: "service-apartment", target: "job-percent", operation: "add" },
    { id: "edge-km-percent", source: "kilometers", target: "job-percent", operation: "add" },
    { id: "edge-percent-distribution", source: "job-percent", target: "distribution", operation: "add" },
    { id: "edge-distribution-total", source: "distribution", target: "total", operation: "add" },
  ],
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const getStoredGraph = (calculationId) => {
  if (!calculationId || typeof window === "undefined") return null;

  try {
    const stored = window.localStorage.getItem(`${STORAGE_PREFIX}${calculationId}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn("Could not read module graph draft", error);
    return null;
  }
};

const storeGraph = (calculationId, graph) => {
  if (!calculationId || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${calculationId}`, JSON.stringify(graph));
  } catch (error) {
    console.warn("Could not store module graph draft", error);
  }
};

const createNode = (type, x = 120, y = 140) => {
  const definition = NODE_DEFINITIONS[type] || NODE_DEFINITIONS.service;
  const suffix = `${Date.now()}-${Math.round(Math.random() * 1000)}`;

  return {
    id: `${type}-${suffix}`,
    type,
    title: definition.defaultTitle,
    x,
    y,
    data: { ...definition.defaultData, lockedFields: {} },
  };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const productTitle = (product) =>
  product?.displayTitle ||
  product?.title ||
  product?.name ||
  product?.productName ||
  product?.product_name ||
  "";

const normalizeProductLine = (product, index = 0) => {
  const productId = product?.product_id ?? product?.id ?? null;
  return {
    id: product?.id || productId || `manual-${Date.now()}-${index}`,
    product_id: productId,
    title: productTitle(product) || "Produkt",
    quantity: product?.quantity ?? 1,
    unitPrice: product?.unitPrice ?? product?.unit_price ?? product?.sales_price ?? 0,
    purchasePrice: product?.purchasePrice ?? product?.purchase_price ?? 0,
    included: product?.included !== false,
  };
};

const productLinesTotal = (products = []) =>
  products.reduce((sum, product) => {
    if (product?.included === false) return sum;
    return sum + toNumber(product.quantity) * toNumber(product.unitPrice);
  }, 0);

const evaluateFunction = (normalized, functionName, callback) =>
  normalized.replace(new RegExp(`\\b${functionName}\\s*\\(([^)]*)\\)`, "gi"), (_, values) => {
    const parsedValues = values.split(",").map((value) => toNumber(value.trim()));
    return callback(parsedValues).toString();
  });

const evaluateExpression = (expression, variables) => {
  const replacements = {
    input: variables.input,
    quantity: variables.quantity,
    price: variables.price,
    percent: variables.percent,
  };

  let normalized = String(expression || "0");

  Object.entries(replacements).forEach(([key, value]) => {
    normalized = normalized.replace(new RegExp(`\\b${key}\\b`, "gi"), String(toNumber(value)));
  });

  normalized = evaluateFunction(normalized, "SUM", (values) => values.reduce((sum, value) => sum + value, 0));
  normalized = evaluateFunction(normalized, "ROUND", (values) => Math.round(values[0] || 0));
  normalized = evaluateFunction(normalized, "MIN", (values) => Math.min(...values));
  normalized = evaluateFunction(normalized, "MAX", (values) => Math.max(...values));

  if (!/^[0-9+\-*/().,\s]+$/.test(normalized)) {
    return { value: 0, warning: "Formula contains unsupported characters" };
  }

  try {
    // Frontend preview only. Backend will own final formula evaluation.
    // eslint-disable-next-line no-new-func
    const value = Function(`"use strict"; return (${normalized});`)();
    return { value: toNumber(value), warning: null };
  } catch (error) {
    return { value: 0, warning: "Formula could not be calculated" };
  }
};

const calculateGraph = (graph) => {
  const nodes = graph?.nodes || [];
  const edges = graph?.edges || [];
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const values = {};
  const warnings = [];
  const visiting = new Set();
  const visited = new Set();

  const incomingFor = (nodeId) => edges.filter((edge) => edge.target === nodeId);

  const evaluateNode = (nodeId) => {
    if (visited.has(nodeId)) return values[nodeId] || 0;
    if (visiting.has(nodeId)) {
      warnings.push(`Circular reference detected around ${nodeMap.get(nodeId)?.title || nodeId}`);
      return 0;
    }

    const node = nodeMap.get(nodeId);
    if (!node) return 0;

    visiting.add(nodeId);

    const incomingTotal = incomingFor(nodeId).reduce((sum, edge) => {
      const sourceValue = evaluateNode(edge.source);
      return edge.operation === "subtract" ? sum - sourceValue : sum + sourceValue;
    }, 0);

    let value = 0;
    const data = node.data || {};

    switch (node.type) {
      case "service":
        value = data.enabled === false ? 0 : toNumber(data.amount);
        break;
      case "quantity":
      case "productPrice":
        value = toNumber(data.quantity) * toNumber(data.unitPrice);
        break;
      case "productCatalog":
        value = productLinesTotal(data.products || []);
        break;
      case "laborHours":
        value = toNumber(data.hours) * toNumber(data.hourlyRate);
        break;
      case "percentage":
        value = incomingTotal * (toNumber(data.percent) / 100);
        break;
      case "distribution":
        value = data.enabled === false || toNumber(data.divisor) <= 0 ? incomingTotal : incomingTotal / toNumber(data.divisor);
        break;
      case "formula": {
        const result = evaluateExpression(data.expression, {
          input: incomingTotal,
          quantity: data.quantity,
          price: data.price,
          percent: data.percent,
        });
        value = result.value;
        if (result.warning) warnings.push(`${node.title}: ${result.warning}`);
        break;
      }
      case "subtotal":
      case "total":
        value = incomingTotal;
        break;
      default:
        value = 0;
    }

    visiting.delete(nodeId);
    visited.add(nodeId);
    values[nodeId] = value;
    return value;
  };

  nodes.forEach((node) => evaluateNode(node.id));

  const totalNodes = nodes.filter((node) => node.type === "total");
  const total = totalNodes.length
    ? totalNodes.reduce((sum, node) => sum + toNumber(values[node.id]), 0)
    : nodes.reduce((sum, node) => sum + (edges.some((edge) => edge.source === node.id) ? 0 : toNumber(values[node.id])), 0);

  return {
    total,
    values,
    warnings,
    breakdown: nodes.map((node) => ({
      id: node.id,
      title: node.title,
      type: node.type,
      value: values[node.id] || 0,
    })),
    calculated_at: new Date().toISOString(),
  };
};

const nodeCenter = (node) => ({
  x: toNumber(node.x) + 115,
  y: toNumber(node.y) + 70,
});

const getConnectionPoint = (node, side) => {
  const width = 230;
  const height = 140;
  const x = toNumber(node.x);
  const y = toNumber(node.y);

  switch (side) {
    case "top":
      return { x: x + width / 2, y };
    case "bottom":
      return { x: x + width / 2, y: y + height };
    case "left":
      return { x, y: y + height / 2 };
    case "right":
    default:
      return { x: x + width, y: y + height / 2 };
  }
};

const preferredSidesForEdge = (source, target) => {
  const sourceCenter = nodeCenter(source);
  const targetCenter = nodeCenter(target);
  const dx = targetCenter.x - sourceCenter.x;
  const dy = targetCenter.y - sourceCenter.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { fromSide: "right", toSide: "left" }
      : { fromSide: "left", toSide: "right" };
  }

  return dy >= 0
    ? { fromSide: "bottom", toSide: "top" }
    : { fromSide: "top", toSide: "bottom" };
};

const calculateOrthogonalPath = (fromPos, toPos, fromSide, toSide) => {
  const spacing = 34;
  let path = `M ${fromPos.x} ${fromPos.y}`;
  const horizontalDistance = Math.abs(toPos.x - fromPos.x);
  const verticalDistance = Math.abs(toPos.y - fromPos.y);

  if ((fromSide === "top" || fromSide === "bottom") && (toSide === "top" || toSide === "bottom")) {
    const fromOffsetY = fromPos.y + (fromSide === "bottom" ? spacing : -spacing);
    const toOffsetY = toPos.y + (toSide === "bottom" ? spacing : -spacing);
    const midY = horizontalDistance < 200 ? (fromPos.y + toPos.y) / 2 : Math.abs(fromOffsetY - fromPos.y) > Math.abs(toOffsetY - toPos.y) ? fromOffsetY : toOffsetY;
    path += ` L ${fromPos.x} ${midY}`;
    path += ` L ${toPos.x} ${midY}`;
    path += ` L ${toPos.x} ${toPos.y}`;
    return path;
  }

  if ((fromSide === "left" || fromSide === "right") && (toSide === "left" || toSide === "right")) {
    const fromOffsetX = fromPos.x + (fromSide === "right" ? spacing : -spacing);
    const toOffsetX = toPos.x + (toSide === "right" ? spacing : -spacing);
    const midX = verticalDistance < 200 ? (fromPos.x + toPos.x) / 2 : Math.abs(fromOffsetX - fromPos.x) > Math.abs(toOffsetX - toPos.x) ? fromOffsetX : toOffsetX;
    path += ` L ${midX} ${fromPos.y}`;
    path += ` L ${midX} ${toPos.y}`;
    path += ` L ${toPos.x} ${toPos.y}`;
    return path;
  }

  if (fromSide === "top" || fromSide === "bottom") {
    const fromOffsetY = fromPos.y + (fromSide === "bottom" ? spacing : -spacing);
    path += ` L ${fromPos.x} ${fromOffsetY}`;
    path += ` L ${toPos.x} ${fromOffsetY}`;
    path += ` L ${toPos.x} ${toPos.y}`;
    return path;
  }

  const fromOffsetX = fromPos.x + (fromSide === "right" ? spacing : -spacing);
  path += ` L ${fromOffsetX} ${fromPos.y}`;
  path += ` L ${fromOffsetX} ${toPos.y}`;
  path += ` L ${toPos.x} ${toPos.y}`;
  return path;
};

const Field = ({
  label,
  value,
  type = "text",
  options,
  locked,
  lockedLabel = "Locked",
  unlockedLabel = "Open",
  onChange,
  onToggleLock,
}) => (
  <div className="offer-module-builder__field">
    <label>{label}</label>
    <div className="offer-module-builder__field-row">
      {options ? (
        <select disabled={locked} value={value || ""} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea disabled={locked} value={value || ""} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input
          disabled={locked}
          type={type}
          value={value ?? ""}
          onChange={(event) => onChange(type === "number" ? event.target.value : event.target.value)}
        />
      )}
      {onToggleLock ? (
        <button
          className="offer-module-builder__lock"
          type="button"
          onClick={onToggleLock}
          title={locked ? lockedLabel : unlockedLabel}
        >
          {locked ? lockedLabel : unlockedLabel}
        </button>
      ) : null}
    </div>
  </div>
);

const UserInsertField = ({ field, locked, onChange, t, value }) => {
  const isBooleanField = field.key === "enabled" || field.type === "boolean";
  const isEnabled = value !== false && value !== "false";

  if (isBooleanField) {
    return (
      <div className="offer-module-builder__field">
        <label>{field.label}</label>
        <button
          className={[
            "offer-module-builder__boolean-button",
            isEnabled ? "is-active" : "is-inactive",
          ].join(" ")}
          disabled={locked}
          type="button"
          onClick={() => onChange(!isEnabled)}
        >
          {isEnabled ? t("offer.calculation.module.enabled") : t("offer.calculation.module.disabled")}
        </button>
      </div>
    );
  }

  return (
    <Field
      label={field.label}
      locked={locked}
      onChange={onChange}
      options={field.options}
      type={field.type}
      value={value}
    />
  );
};

const ProductLinesEditor = ({ locked, products = [], onChange, t }) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const lines = Array.isArray(products) ? products : [];

  const updateLine = (index, patch) => {
    onChange(lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)));
  };

  const removeLine = (index) => {
    onChange(lines.filter((_, lineIndex) => lineIndex !== index));
  };

  const addManualLine = () => {
    onChange([...lines, normalizeProductLine({ title: t("offer.calculation.module.product.manual_title") }, lines.length)]);
  };

  const addSelectedProducts = () => {
    const nextProducts = selectedProducts.map((product, index) => normalizeProductLine(product, lines.length + index));
    onChange([...lines, ...nextProducts]);
    setSelectedProducts([]);
    setSelectorOpen(false);
  };

  return (
    <div className="offer-module-builder__product-editor">
      <div className="offer-module-builder__product-editor-header">
        <div>
          <strong>{t("offer.calculation.module.product.title")}</strong>
          <span>{t("offer.calculation.module.product.hint")}</span>
        </div>
        <div className="offer-module-builder__product-actions">
          <button className="offer-module-builder__button" disabled={locked} type="button" onClick={() => setSelectorOpen(true)}>
            {t("offer.calculation.module.product.add_from_catalog")}
          </button>
          <button className="offer-module-builder__button" disabled={locked} type="button" onClick={addManualLine}>
            {t("offer.calculation.module.product.add_manual")}
          </button>
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="offer-module-builder__empty-line">{t("offer.calculation.module.product.empty")}</div>
      ) : null}

      {lines.map((line, index) => (
        <div className="offer-module-builder__product-line" key={`${line.product_id || line.id || "line"}-${index}`}>
          <input
            disabled={locked || Boolean(line.product_id)}
            placeholder={t("offer.calculation.module.field.product_name")}
            value={line.title || ""}
            onChange={(event) => updateLine(index, { title: event.target.value })}
          />
          <input
            disabled={locked}
            min="0"
            type="number"
            value={line.quantity ?? 1}
            onChange={(event) => updateLine(index, { quantity: event.target.value })}
          />
          <input
            disabled={locked}
            min="0"
            type="number"
            value={line.unitPrice ?? 0}
            onChange={(event) => updateLine(index, { unitPrice: event.target.value })}
          />
          <button
            className="offer-module-builder__line-toggle"
            disabled={locked}
            type="button"
            onClick={() => updateLine(index, { included: line.included === false })}
          >
            {line.included === false
              ? t("offer.calculation.module.product.include")
              : t("offer.calculation.module.product.exclude")}
          </button>
          <button
            className="offer-module-builder__line-remove"
            disabled={locked}
            type="button"
            onClick={() => removeLine(index)}
          >
            x
          </button>
        </div>
      ))}

      <div className="offer-module-builder__product-total">
        <span>{t("offer.calculation.module.product.total")}</span>
        <strong>{formatCurrency(productLinesTotal(lines))}</strong>
      </div>

      <Dialog open={selectorOpen} onClose={() => setSelectorOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>{t("offer.calculation.module.product.add_from_catalog")}</DialogTitle>
        <DialogContent dividers>
          <ModernProductSelectorV2
            embeddedInParentDialog
            multiple
            onChange={setSelectedProducts}
            onClose={() => setSelectorOpen(false)}
            open={selectorOpen}
            selectedProducts={selectedProducts}
            showHeader
            showWarehouseOnlyDefault={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectorOpen(false)}>{t("offer.calculation.module.product.cancel")}</Button>
          <Button disabled={!selectedProducts.length} onClick={addSelectedProducts} variant="contained">
            {t("offer.calculation.module.product.add_selected")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const editableFieldsForNode = (node, t) => {
  const fields = {
    service: [
      { key: "enabled", type: "select", label: t("offer.calculation.module.field.enabled"), options: [
        { value: "true", label: t("offer.calculation.module.enabled") },
        { value: "false", label: t("offer.calculation.module.disabled") },
      ] },
      { key: "amount", type: "number", label: t("offer.calculation.module.field.amount") },
    ],
    quantity: [
      { key: "quantity", type: "number", label: t("offer.calculation.module.field.quantity") },
      { key: "unitPrice", type: "number", label: t("offer.calculation.module.field.unit_price") },
    ],
    productPrice: [
      { key: "productName", type: "text", label: t("offer.calculation.module.field.product_name") },
      { key: "quantity", type: "number", label: t("offer.calculation.module.field.quantity") },
      { key: "unitPrice", type: "number", label: t("offer.calculation.module.field.unit_price") },
    ],
    laborHours: [
      { key: "hours", type: "number", label: t("offer.calculation.module.field.hours") },
      { key: "hourlyRate", type: "number", label: t("offer.calculation.module.field.hourly_rate") },
      { key: "costHourlyRate", type: "number", label: t("offer.calculation.module.field.cost_hourly_rate") },
    ],
    percentage: [
      { key: "percent", type: "number", label: t("offer.calculation.module.field.percent") },
      { key: "scope", type: "select", label: t("offer.calculation.module.field.scope"), options: [
        { value: "all", label: t("offer.calculation.module.scope.all") },
        { value: "work", label: t("offer.calculation.module.scope.work") },
        { value: "driving", label: t("offer.calculation.module.scope.driving") },
        { value: "selected", label: t("offer.calculation.module.scope.selected") },
      ] },
    ],
    distribution: [
      { key: "enabled", type: "boolean", label: t("offer.calculation.module.field.enabled") },
      { key: "divisor", type: "number", label: t("offer.calculation.module.field.divisor") },
    ],
    formula: [
      { key: "quantity", type: "number", label: t("offer.calculation.module.field.quantity") },
      { key: "price", type: "number", label: t("offer.calculation.module.field.unit_price") },
      { key: "percent", type: "number", label: t("offer.calculation.module.field.percent") },
    ],
  };

  return fields[node.type] || [];
};

const graphPreviewBounds = (graph) => {
  const padding = 160;
  const nodeWidth = 220;
  const nodeHeight = 118;
  const nodes = graph.nodes || [];

  if (!nodes.length) {
    return {
      height: 560,
      minX: 0,
      minY: 0,
      width: 1180,
    };
  }

  const minX = Math.min(...nodes.map((node) => toNumber(node.x))) - padding;
  const minY = Math.min(...nodes.map((node) => toNumber(node.y))) - padding;
  const maxX = Math.max(...nodes.map((node) => toNumber(node.x) + nodeWidth)) + padding;
  const maxY = Math.max(...nodes.map((node) => toNumber(node.y) + nodeHeight)) + padding;

  return {
    height: Math.max(560, maxY - minY),
    minX,
    minY,
    width: Math.max(1180, maxX - minX),
  };
};

const GraphPreview = ({ graph, result, onSelectEdge }) => {
  const bounds = graphPreviewBounds(graph);

  return (
    <svg
      className="offer-module-builder__insert-map"
      style={{ height: bounds.height, minWidth: bounds.width, width: bounds.width }}
      viewBox={`${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`}
    >
      <defs>
        <marker id="module-insert-arrow-add" markerHeight="8" markerWidth="8" orient="auto" refX="7" refY="4">
          <path d="M0,0 L8,4 L0,8 z" fill="#0f766e" />
        </marker>
        <marker id="module-insert-arrow-subtract" markerHeight="8" markerWidth="8" orient="auto" refX="7" refY="4">
          <path d="M0,0 L8,4 L0,8 z" fill="#b91c1c" />
        </marker>
      </defs>
      {graph.edges.map((edge) => {
        const source = graph.nodes.find((node) => node.id === edge.source);
        const target = graph.nodes.find((node) => node.id === edge.target);
        if (!source || !target) return null;

        const { fromSide, toSide } = preferredSidesForEdge(source, target);
        const start = getConnectionPoint(source, fromSide);
        const end = getConnectionPoint(target, toSide);
        const path = calculateOrthogonalPath(start, end, fromSide, toSide);
        const color = edge.operation === "subtract" ? "#b91c1c" : "#0f766e";
        const marker = edge.operation === "subtract" ? "url(#module-insert-arrow-subtract)" : "url(#module-insert-arrow-add)";

        return (
          <g key={edge.id}>
            <path d={path} fill="none" stroke="transparent" strokeWidth="12" onClick={() => onSelectEdge?.(edge)} />
            <path d={path} fill="none" markerEnd={marker} stroke={color} strokeWidth="2.5" />
          </g>
        );
      })}
      {graph.nodes.map((node) => (
        <foreignObject height="118" key={node.id} width="220" x={node.x} y={node.y}>
          <div className="offer-module-builder__insert-node">
            <span>{node.title}</span>
            <strong>{formatCurrency(result.values[node.id])}</strong>
          </div>
        </foreignObject>
      ))}
    </svg>
  );
};

const FormulaAssistant = ({ node, data, locked, onChange, t }) => {
  const expression = data.expression || "";
  const appendToken = (token) => {
    const spacer = expression && !expression.endsWith(" ") ? " " : "";
    onChange(`${expression}${spacer}${token}`);
  };

  return (
    <div className="offer-module-builder__formula-assistant">
      <div className="offer-module-builder__formula-header">
        <span>{t("offer.calculation.module.formula.assistant_title")}</span>
        <span>{t("offer.calculation.module.formula.assistant_subtitle")}</span>
      </div>

      <div className="offer-module-builder__formula-block">
        <label>{t("offer.calculation.module.formula.templates")}</label>
        <div className="offer-module-builder__formula-grid">
          {FORMULA_TEMPLATES.map((template) => (
            <button
              className="offer-module-builder__formula-chip"
              disabled={locked}
              key={template.expression}
              type="button"
              onClick={() => onChange(template.expression)}
            >
              {t(template.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="offer-module-builder__formula-block">
        <label>{t("offer.calculation.module.formula.variables")}</label>
        <div className="offer-module-builder__formula-grid is-compact">
          {FORMULA_VARIABLES.map((variable) => (
            <button
              className="offer-module-builder__formula-chip"
              disabled={locked}
              key={variable.token}
              type="button"
              onClick={() => appendToken(variable.token)}
            >
              {variable.token}
              <small>{t(variable.labelKey)}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="offer-module-builder__formula-block">
        <label>{t("offer.calculation.module.formula.functions")}</label>
        <div className="offer-module-builder__formula-grid">
          {FORMULA_FUNCTIONS.map((formulaFunction) => (
            <button
              className="offer-module-builder__formula-chip"
              disabled={locked}
              key={formulaFunction.token}
              type="button"
              onClick={() => onChange(formulaFunction.token)}
            >
              {t(formulaFunction.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="offer-module-builder__formula-example">
        <strong>{t("offer.calculation.module.formula.example_title")}</strong>
        <span>{t("offer.calculation.module.formula.example_text", { title: node.title })}</span>
      </div>
    </div>
  );
};

export const OfferCalculationModuleBuilder = ({ values, setFieldValue }) => {
  const { t } = useTranslation();
  const calculationId = values?.id || "new";
  const initialGraph = useMemo(() => {
    if (values?.module_graph?.nodes?.length) return values.module_graph;

    return values?.id ? DEFAULT_GRAPH : getStoredGraph(calculationId) || DEFAULT_GRAPH;
  }, [calculationId, values?.id, values?.module_graph]);

  const [graph, setGraph] = useState(initialGraph);
  const [selectedNodeId, setSelectedNodeId] = useState(initialGraph.nodes?.[0]?.id || null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState(null);
  const [connectSourceId, setConnectSourceId] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [panState, setPanState] = useState(null);
  const workspaceRef = useRef(null);

  const result = useMemo(() => calculateGraph(graph), [graph]);
  const selectedNode = graph.nodes.find((node) => node.id === selectedNodeId);
  const selectedEdge = graph.edges.find((edge) => edge.id === selectedEdgeId);
  const viewport = graph.viewport || DEFAULT_VIEWPORT;

  useEffect(() => {
    setFieldValue("module_enabled", true);
    setFieldValue("module_graph", graph);
    setFieldValue("module_result", result);
    if (!values?.id) {
      storeGraph(calculationId, graph);
    }
  }, [calculationId, graph, result, setFieldValue, values?.id]);

  useEffect(() => {
    if (!edgeContextMenu) return undefined;

    const closeMenu = () => setEdgeContextMenu(null);
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("contextmenu", closeMenu);

    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("contextmenu", closeMenu);
    };
  }, [edgeContextMenu]);

  const updateGraph = (updater) => {
    setGraph((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...next, viewport: next.viewport || current.viewport || DEFAULT_VIEWPORT, version: 1 };
    });
  };

  const updateViewport = (updater) => {
    updateGraph((current) => {
      const currentViewport = current.viewport || DEFAULT_VIEWPORT;
      const nextViewport = typeof updater === "function" ? updater(currentViewport) : updater;
      return {
        ...current,
        viewport: {
          x: nextViewport.x,
          y: nextViewport.y,
          zoom: clamp(nextViewport.zoom, 0.25, 2.5),
        },
      };
    });
  };

  const screenToWorld = (clientX, clientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const currentViewport = graph.viewport || DEFAULT_VIEWPORT;
    return {
      x: (clientX - rect.left - currentViewport.x) / currentViewport.zoom,
      y: (clientY - rect.top - currentViewport.y) / currentViewport.zoom,
    };
  };

  const zoomCanvas = (factor, centerClientX, centerClientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    const centerX = centerClientX || (rect ? rect.left + rect.width / 2 : 0);
    const centerY = centerClientY || (rect ? rect.top + rect.height / 2 : 0);
    const before = screenToWorld(centerX, centerY);

    updateViewport((current) => {
      const nextZoom = clamp(current.zoom * factor, 0.25, 2.5);
      const canvasX = rect ? centerX - rect.left : 0;
      const canvasY = rect ? centerY - rect.top : 0;
      return {
        zoom: nextZoom,
        x: canvasX - before.x * nextZoom,
        y: canvasY - before.y * nextZoom,
      };
    });
  };

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      zoomCanvas(event.deltaY > 0 ? 0.92 : 1.08, event.clientX, event.clientY);
    };

    workspace.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      workspace.removeEventListener("wheel", handleWheel);
    };
  }, [zoomCanvas]);

  const updateNode = (nodeId, patch) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, ...patch } : node)),
    }));
  };

  const updateNodeData = (nodeId, field, value) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, [field]: value } } : node
      ),
    }));
  };

  const toggleFieldLock = (nodeId, field) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const lockedFields = { ...(node.data?.lockedFields || {}) };
        lockedFields[field] = !lockedFields[field];
        return { ...node, data: { ...node.data, lockedFields } };
      }),
    }));
  };

  const addNode = (type, x, y) => {
    const node = createNode(type, x, y);
    updateGraph((current) => ({ ...current, nodes: [...current.nodes, node] }));
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
  };

  const deleteNode = (nodeId) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.filter((node) => node.id !== nodeId),
      edges: current.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
    }));
    setSelectedNodeId(null);
  };

  const connectToNode = (targetId) => {
    if (!connectSourceId || connectSourceId === targetId) {
      setConnectSourceId(targetId);
      return;
    }

    updateGraph((current) => {
      const exists = current.edges.some((edge) => edge.source === connectSourceId && edge.target === targetId);
      if (exists) return current;

      return {
        ...current,
        edges: [
          ...current.edges,
          {
            id: `edge-${connectSourceId}-${targetId}-${Date.now()}`,
            source: connectSourceId,
            target: targetId,
            operation: "add",
          },
        ],
      };
    });
    setConnectSourceId(null);
  };

  const updateEdge = (edgeId, patch) => {
    updateGraph((current) => ({
      ...current,
      edges: current.edges.map((edge) => (edge.id === edgeId ? { ...edge, ...patch } : edge)),
    }));
  };

  const deleteEdge = (edgeId) => {
    updateGraph((current) => ({ ...current, edges: current.edges.filter((edge) => edge.id !== edgeId) }));
    setSelectedEdgeId(null);
    setEdgeContextMenu(null);
  };

  const openEdgeContextMenu = (edge, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
    setEdgeContextMenu({
      edgeId: edge.id,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const onDrop = (event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData("application/promang-module-node");
    if (!type || !workspaceRef.current) return;

    const point = screenToWorld(event.clientX, event.clientY);
    addNode(type, point.x - 110, point.y - 40);
  };

  const onPointerMove = (event) => {
    if (dragState) {
      const currentViewport = graph.viewport || DEFAULT_VIEWPORT;
      const dx = (event.clientX - dragState.clientX) / currentViewport.zoom;
      const dy = (event.clientY - dragState.clientY) / currentViewport.zoom;
      updateNode(dragState.nodeId, {
        x: Math.max(0, dragState.x + dx),
        y: Math.max(58, dragState.y + dy),
      });
      return;
    }

    if (panState) {
      updateViewport({
        ...viewport,
        x: panState.x + event.clientX - panState.clientX,
        y: panState.y + event.clientY - panState.clientY,
      });
    }
  };

  const startPan = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest(".offer-module-builder__node")) return;
    if (event.target.closest(".offer-module-builder__edge")) return;
    if (event.target.closest(".offer-module-builder__edge-label")) return;
    if (event.target.closest(".offer-module-builder__context-menu")) return;
    setSelectedEdgeId(null);
    setSelectedNodeId(null);
    setEdgeContextMenu(null);
    setPanState({
      clientX: event.clientX,
      clientY: event.clientY,
      x: viewport.x,
      y: viewport.y,
    });
  };

  const stopDrag = () => {
    setDragState(null);
    setPanState(null);
  };

  const renderNodeFields = (node) => {
    const data = node.data || {};
    const isLocked = (field) => data.lockedFields?.[field];
    const lockToggle = (field) => () => toggleFieldLock(node.id, field);
    const lockLabels = {
      lockedLabel: t("offer.calculation.module.locked"),
      unlockedLabel: t("offer.calculation.module.unlocked"),
    };

    return (
      <>
        <Field
          label={t("offer.calculation.module.field.title")}
          value={node.title}
          onChange={(value) => updateNode(node.id, { title: value })}
        />

        {node.type === "service" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.amount")}
              type="number"
              value={data.amount}
              locked={isLocked("amount")}
              onChange={(value) => updateNodeData(node.id, "amount", value)}
              onToggleLock={lockToggle("amount")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.enabled")}
              value={data.enabled === false ? "false" : "true"}
              options={[
                { value: "true", label: t("offer.calculation.module.enabled") },
                { value: "false", label: t("offer.calculation.module.disabled") },
              ]}
              locked={isLocked("enabled")}
              onChange={(value) => updateNodeData(node.id, "enabled", value === "true")}
              onToggleLock={lockToggle("enabled")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "quantity" || node.type === "productPrice" ? (
          <>
            {node.type === "productPrice" ? (
              <Field
                label={t("offer.calculation.module.field.product_name")}
                value={data.productName}
                locked={isLocked("productName")}
                onChange={(value) => updateNodeData(node.id, "productName", value)}
                onToggleLock={lockToggle("productName")}
                {...lockLabels}
              />
            ) : null}
            <Field
              label={t("offer.calculation.module.field.quantity")}
              type="number"
              value={data.quantity}
              locked={isLocked("quantity")}
              onChange={(value) => updateNodeData(node.id, "quantity", value)}
              onToggleLock={lockToggle("quantity")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.unit_price")}
              type="number"
              value={data.unitPrice}
              locked={isLocked("unitPrice")}
              onChange={(value) => updateNodeData(node.id, "unitPrice", value)}
              onToggleLock={lockToggle("unitPrice")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "productCatalog" ? (
          <>
            <ProductLinesEditor
              locked={isLocked("products")}
              products={data.products || []}
              onChange={(products) => updateNodeData(node.id, "products", products)}
              t={t}
            />
            <button className="offer-module-builder__lock mt-2" type="button" onClick={lockToggle("products")}>
              {isLocked("products") ? t("offer.calculation.module.locked") : t("offer.calculation.module.unlocked")}
            </button>
          </>
        ) : null}

        {node.type === "laborHours" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.hours")}
              type="number"
              value={data.hours}
              locked={isLocked("hours")}
              onChange={(value) => updateNodeData(node.id, "hours", value)}
              onToggleLock={lockToggle("hours")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.hourly_rate")}
              type="number"
              value={data.hourlyRate}
              locked={isLocked("hourlyRate")}
              onChange={(value) => updateNodeData(node.id, "hourlyRate", value)}
              onToggleLock={lockToggle("hourlyRate")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.cost_hourly_rate")}
              type="number"
              value={data.costHourlyRate}
              locked={isLocked("costHourlyRate")}
              onChange={(value) => updateNodeData(node.id, "costHourlyRate", value)}
              onToggleLock={lockToggle("costHourlyRate")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "percentage" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.percent")}
              type="number"
              value={data.percent}
              locked={isLocked("percent")}
              onChange={(value) => updateNodeData(node.id, "percent", value)}
              onToggleLock={lockToggle("percent")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.scope")}
              value={data.scope || "all"}
              options={[
                { value: "all", label: t("offer.calculation.module.scope.all") },
                { value: "work", label: t("offer.calculation.module.scope.work") },
                { value: "driving", label: t("offer.calculation.module.scope.driving") },
                { value: "selected", label: t("offer.calculation.module.scope.selected") },
              ]}
              locked={isLocked("scope")}
              onChange={(value) => updateNodeData(node.id, "scope", value)}
              onToggleLock={lockToggle("scope")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "distribution" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.enabled")}
              value={data.enabled === false ? "false" : "true"}
              options={[
                { value: "true", label: t("offer.calculation.module.enabled") },
                { value: "false", label: t("offer.calculation.module.disabled") },
              ]}
              locked={isLocked("enabled")}
              onChange={(value) => updateNodeData(node.id, "enabled", value === "true")}
              onToggleLock={lockToggle("enabled")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.divisor")}
              type="number"
              value={data.divisor}
              locked={isLocked("divisor")}
              onChange={(value) => updateNodeData(node.id, "divisor", value)}
              onToggleLock={lockToggle("divisor")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "formula" ? (
          <>
            <FormulaAssistant
              data={data}
              locked={isLocked("expression")}
              node={node}
              onChange={(value) => updateNodeData(node.id, "expression", value)}
              t={t}
            />
            <Field
              label={t("offer.calculation.module.field.expression")}
              value={data.expression}
              locked={isLocked("expression")}
              onChange={(value) => updateNodeData(node.id, "expression", value)}
              onToggleLock={lockToggle("expression")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "note" ? (
          <Field
            label={t("offer.calculation.module.field.note")}
            type="textarea"
            value={data.note}
            locked={isLocked("note")}
            onChange={(value) => updateNodeData(node.id, "note", value)}
            onToggleLock={lockToggle("note")}
            {...lockLabels}
          />
        ) : null}
      </>
    );
  };

  return (
    <div className="offer-module-builder">
      <aside className="offer-module-builder__panel">
        <h3 className="offer-module-builder__section-title">{t("offer.calculation.module.palette_title")}</h3>
        <p className="offer-module-builder__hint">{t("offer.calculation.module.palette_hint")}</p>
        <div className="mt-4">
          {Object.entries(NODE_DEFINITIONS).map(([type, definition]) => (
            <button
              className="offer-module-builder__palette-item"
              draggable
              key={type}
              type="button"
              onClick={() => addNode(type)}
              onDragStart={(event) => event.dataTransfer.setData("application/promang-module-node", type)}
            >
              <span>
                <span className="block">{t(definition.labelKey)}</span>
                <span className="offer-module-builder__hint">{t(definition.descriptionKey)}</span>
              </span>
              <span>+</span>
            </button>
          ))}
        </div>
      </aside>

      <section className="offer-module-builder__canvas">
        <div className="offer-module-builder__canvas-toolbar">
          <div>
            <h3 className="offer-module-builder__section-title">{t("offer.calculation.module.canvas_title")}</h3>
            <p className="offer-module-builder__hint">
              {connectSourceId
                ? t("offer.calculation.module.connecting_hint")
                : t("offer.calculation.module.canvas_hint")}
            </p>
          </div>
          <div className="offer-module-builder__toolbar-actions">
            <button className="offer-module-builder__button" type="button" onClick={() => zoomCanvas(0.85)}>
              {t("offer.calculation.module.zoom_out")}
            </button>
            <span className="offer-module-builder__zoom-label">
              {Math.round(viewport.zoom * 100)}%
            </span>
            <button className="offer-module-builder__button" type="button" onClick={() => zoomCanvas(1.15)}>
              {t("offer.calculation.module.zoom_in")}
            </button>
            <button className="offer-module-builder__button" type="button" onClick={() => updateViewport(DEFAULT_VIEWPORT)}>
              {t("offer.calculation.module.reset_view")}
            </button>
            <button className="offer-module-builder__button" type="button" onClick={() => updateGraph(DEFAULT_GRAPH)}>
              {t("offer.calculation.module.reset_example")}
            </button>
          </div>
        </div>

        <div
          className="offer-module-builder__workspace"
          onDragOver={(event) => event.preventDefault()}
          onDrop={onDrop}
          onPointerMove={onPointerMove}
          onPointerDown={startPan}
          onPointerUp={stopDrag}
          onPointerLeave={stopDrag}
          ref={workspaceRef}
        >
          <div
            className="offer-module-builder__world"
            style={{
              height: CANVAS_SIZE,
              transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
              width: CANVAS_SIZE,
            }}
          >
            <svg className="offer-module-builder__edges" height={CANVAS_SIZE} width={CANVAS_SIZE}>
              <defs>
                <filter id="module-edge-shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="1" floodColor="#0f172a" floodOpacity="0.16" stdDeviation="1.5" />
                </filter>
                <marker id="module-arrow-add" markerHeight="10" markerWidth="10" orient="auto" refX="9" refY="5">
                  <path d="M0,0 L10,5 L0,10 z" fill="#0f766e" />
                </marker>
                <marker id="module-arrow-subtract" markerHeight="10" markerWidth="10" orient="auto" refX="9" refY="5">
                  <path d="M0,0 L10,5 L0,10 z" fill="#b91c1c" />
                </marker>
              </defs>
              {graph.edges.map((edge) => {
                const source = graph.nodes.find((node) => node.id === edge.source);
                const target = graph.nodes.find((node) => node.id === edge.target);
                if (!source || !target) return null;

                const { fromSide, toSide } = preferredSidesForEdge(source, target);
                const start = getConnectionPoint(source, fromSide);
                const end = getConnectionPoint(target, toSide);
                const midX = (start.x + end.x) / 2;
                const midY = (start.y + end.y) / 2;
                const color = edge.operation === "subtract" ? "#b91c1c" : "#0f766e";
                const marker = edge.operation === "subtract" ? "url(#module-arrow-subtract)" : "url(#module-arrow-add)";
                const path = calculateOrthogonalPath(start, end, fromSide, toSide);
                const isSelected = selectedEdgeId === edge.id;

                return (
                  <g
                    className="offer-module-builder__edge"
                    key={edge.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedEdgeId(edge.id);
                      setSelectedNodeId(null);
                    }}
                    onContextMenu={(event) => openEdgeContextMenu(edge, event)}
                  >
                    <path
                      d={path}
                      fill="none"
                      stroke="transparent"
                      strokeWidth="22"
                      style={{ cursor: "pointer", pointerEvents: "stroke" }}
                    />
                    <path
                      className="offer-module-builder__edge-visible"
                      d={path}
                      fill="none"
                      filter="url(#module-edge-shadow)"
                      markerEnd={marker}
                      pointerEvents="none"
                      stroke={color}
                      strokeDasharray={edge.operation === "subtract" ? "8 5" : undefined}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={isSelected ? "4" : "3"}
                    />
                    <g className="offer-module-builder__edge-label" onContextMenu={(event) => openEdgeContextMenu(edge, event)}>
                      <rect fill="#fff" height="24" rx="12" stroke={color} width="74" x={midX - 37} y={midY - 12} />
                      <text fill={color} fontSize="12" fontWeight="700" textAnchor="middle" x={midX} y={midY + 4}>
                        {edge.operation === "subtract" ? "Trekk fra" : "Legg til"}
                      </text>
                    </g>
                  </g>
                );
              })}
            </svg>

            {graph.nodes.map((node) => (
              <div
                className={[
                  "offer-module-builder__node",
                  selectedNodeId === node.id ? "is-selected" : "",
                  connectSourceId === node.id ? "is-connecting" : "",
                ].join(" ")}
                key={node.id}
                onClick={() => {
                  setSelectedNodeId(node.id);
                  setSelectedEdgeId(null);
                }}
                style={{ left: node.x, top: node.y }}
              >
              <div
                className="offer-module-builder__node-header"
                onPointerDown={(event) => {
                  event.preventDefault();
                  setDragState({ nodeId: node.id, clientX: event.clientX, clientY: event.clientY, x: node.x, y: node.y });
                  setSelectedNodeId(node.id);
                }}
              >
                <div>
                  <div className="offer-module-builder__node-title">{node.title}</div>
                  <div className="offer-module-builder__node-type">
                    {t(NODE_DEFINITIONS[node.type]?.labelKey || "offer.calculation.module.node.service")}
                  </div>
                </div>
                <div className="offer-module-builder__node-total">{formatCurrency(result.values[node.id])}</div>
              </div>

              <div className="flex gap-2">
                <button className="offer-module-builder__button" type="button" onClick={() => connectToNode(node.id)}>
                  {connectSourceId && connectSourceId !== node.id
                    ? t("offer.calculation.module.connect_here")
                    : t("offer.calculation.module.connect")}
                </button>
                <button className="offer-module-builder__button is-danger" type="button" onClick={() => deleteNode(node.id)}>
                  {t("offer.calculation.module.remove")}
                </button>
              </div>
            </div>
            ))}
          </div>

          {edgeContextMenu ? (
            <div
              className="offer-module-builder__context-menu"
              onClick={(event) => event.stopPropagation()}
              onContextMenu={(event) => event.preventDefault()}
              style={{ left: edgeContextMenu.x, top: edgeContextMenu.y }}
            >
              <button type="button" onClick={() => deleteEdge(edgeContextMenu.edgeId)}>
                {t("offer.calculation.module.remove_edge")}
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <aside className="offer-module-builder__result">
        <h3 className="offer-module-builder__section-title">{t("offer.calculation.module.result_title")}</h3>
        <div className="text-3xl font-bold text-teal-700">{formatCurrency(result.total)}</div>
        <p className="offer-module-builder__hint mt-2">{t("offer.calculation.module.result_hint")}</p>

        {selectedEdge ? (
          <div className="offer-module-builder__breakdown">
            <h4 className="offer-module-builder__section-title">{t("offer.calculation.module.edge_title")}</h4>
            <Field
              label={t("offer.calculation.module.field.operation")}
              value={selectedEdge.operation}
              options={[
                { value: "add", label: t("offer.calculation.module.operation.add") },
                { value: "subtract", label: t("offer.calculation.module.operation.subtract") },
              ]}
              onChange={(value) => updateEdge(selectedEdge.id, { operation: value })}
            />
            <button className="offer-module-builder__button is-danger mt-2" type="button" onClick={() => deleteEdge(selectedEdge.id)}>
              {t("offer.calculation.module.remove_edge")}
            </button>
          </div>
        ) : null}

        {selectedNode ? (
          <div className="offer-module-builder__breakdown">
            <h4 className="offer-module-builder__section-title">{t("offer.calculation.module.editor_title")}</h4>
            {renderNodeFields(selectedNode)}
          </div>
        ) : null}

        <div className="offer-module-builder__breakdown">
          <h4 className="offer-module-builder__section-title">{t("offer.calculation.module.breakdown_title")}</h4>
          {result.breakdown.map((item) => (
            <div className="offer-module-builder__breakdown-row" key={item.id}>
              <span>{item.title}</span>
              <strong>{formatCurrency(item.value)}</strong>
            </div>
          ))}
        </div>

        {result.warnings.map((warning) => (
          <div className="offer-module-builder__warning" key={warning}>
            {warning}
          </div>
        ))}
      </aside>
    </div>
  );
};

export const OfferCalculationUserInsertView = ({ values, setFieldValue }) => {
  const { t } = useTranslation();
  const calculationId = values?.id || "new";
  const [activeInsertPanel, setActiveInsertPanel] = useState("fields");
  const [graph, setGraph] = useState(() => {
    if (values?.module_graph?.nodes?.length) return values.module_graph;
    return values?.id ? DEFAULT_GRAPH : getStoredGraph(calculationId) || DEFAULT_GRAPH;
  });

  const result = useMemo(() => calculateGraph(graph), [graph]);

  useEffect(() => {
    setFieldValue("module_graph", graph);
    setFieldValue("module_result", result);
    if (!values?.id) {
      storeGraph(calculationId, graph);
    }
  }, [calculationId, graph, result, setFieldValue, values?.id]);

  const updateNodeData = (nodeId, key, value) => {
    setGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const nextValue = key === "enabled" ? value === true || value === "true" : value;
        return { ...node, data: { ...node.data, [key]: nextValue } };
      }),
    }));
  };

  const inputNodes = graph.nodes.filter(
    (node) => editableFieldsForNode(node, t).length > 0 || node.type === "productCatalog"
  );

  return (
    <div className="offer-module-builder__insert-view">
      <div className="offer-module-builder__insert-header">
        <div>
          <h3>{t("offer.calculation.insert.title")}</h3>
          <p>{t("offer.calculation.insert.subtitle")}</p>
        </div>
        <div className="offer-module-builder__insert-total">
          <span>{t("offer.calculation.total")}</span>
          <strong>{formatCurrency(result.total)}</strong>
        </div>
      </div>

      <div className="offer-module-builder__insert-tabs" role="tablist">
        <button
          className={activeInsertPanel === "fields" ? "is-active" : ""}
          type="button"
          onClick={() => setActiveInsertPanel("fields")}
        >
          {t("offer.calculation.insert.fields_tab")}
        </button>
        <button
          className={activeInsertPanel === "map" ? "is-active" : ""}
          type="button"
          onClick={() => setActiveInsertPanel("map")}
        >
          {t("offer.calculation.insert.map_tab")}
        </button>
      </div>

      {activeInsertPanel === "fields" ? (
      <div className="offer-module-builder__insert-grid is-fields-only">
        <div className="offer-module-builder__insert-inputs">
          <h4>{t("offer.calculation.insert.inputs_title")}</h4>
          <p>{t("offer.calculation.insert.inputs_hint")}</p>
          {inputNodes.map((node) => {
            const fields = editableFieldsForNode(node, t);
            const lockedFields = node.data?.lockedFields || {};

            return (
              <div className="offer-module-builder__insert-card" key={node.id}>
                <div className="offer-module-builder__insert-card-header">
                  <span>{node.title}</span>
                  <strong>{formatCurrency(result.values[node.id])}</strong>
                </div>
                {fields.map((field) => (
                  <UserInsertField
                    field={field}
                    key={field.key}
                    locked={Boolean(lockedFields[field.key])}
                    value={field.key === "enabled" ? node.data?.[field.key] !== false : node.data?.[field.key] ?? ""}
                    onChange={(value) => updateNodeData(node.id, field.key, value)}
                    t={t}
                  />
                ))}
                {node.type === "productCatalog" ? (
                  <ProductLinesEditor
                    locked={Boolean(lockedFields.products)}
                    products={node.data?.products || []}
                    onChange={(products) => updateNodeData(node.id, "products", products)}
                    t={t}
                  />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="offer-module-builder__insert-summary">
          <h4>{t("offer.calculation.module.breakdown_title")}</h4>
          {result.breakdown.map((item) => (
            <div className="offer-module-builder__breakdown-row" key={item.id}>
              <span>{item.title}</span>
              <strong>{formatCurrency(item.value)}</strong>
            </div>
          ))}
          {result.warnings.map((warning) => (
            <div className="offer-module-builder__warning" key={warning}>
              {warning}
            </div>
          ))}
        </div>
      </div>
      ) : (
        <div className="offer-module-builder__insert-map-shell is-expanded">
          <div className="offer-module-builder__insert-map-toolbar">
            <div>
              <h4>{t("offer.calculation.insert.flow_title")}</h4>
              <p>{t("offer.calculation.insert.flow_hint")}</p>
            </div>
          </div>
          <GraphPreview graph={graph} result={result} />
        </div>
      )}
    </div>
  );
};

export default OfferCalculationModuleBuilder;
